<template>
  <section class="section-listing-banner">
    <div class="__listing-banner-wrapper">
      <div class="__listing-image-wrapper" @click="isGalleryOpen = true;">
        <div v-if="bannerImages.first" class="__primary-image __column" :style="`background-image: url('${bannerImages.first}')`" />
        <div v-if="bannerImages.fifth" class="__secondary-images __column">
          <div class="__secondary-image-item" :style="`background-image: url('${bannerImages.second}')`" aria-label="listing image" />
          <div class="__secondary-image-item" :style="`background-image: url('${bannerImages.third}')`" aria-label="listing image" />
          <div class="__secondary-image-item" :style="`background-image: url('${bannerImages.forth}')`" aria-label="listing image" />
          <div class="__secondary-image-item" :style="`background-image: url('${bannerImages.fifth}')`" aria-label="listing image">
            <div v-if="listing.gallery.data.length > 5" class="__more-image-count">
              +{{ listing.gallery.data.length - 5 }}
            </div>
          </div>
        </div>
      </div>
      <div class="__go-back-button-wrapper">
        <div class="container">
          <ElementsAvatar class="__go-back-btn" :icon-src="chevronDownIcon" icon-alt="Parking icon" @click="goBack()" />
          <ElementsAvatar :icon-src="galleryIcon" icon-alt="Parking icon" is-clickable @click="isGalleryOpen = true;" />
        </div>
      </div>
      <div class="__banner-content-wrapper">
        <div class="container">
          <h1 class="__listing-title">
            {{ listing.name }}
          </h1>
          <div class="__info-action-bar">
            <div class="__info-bar">
              <div class="__listing-type">
                {{ listing.listingType }}
              </div>
              <div class="__listing-views">
                <img v-lazy-load  :data-src="eyeIcon" alt="View icon">
                Views: <BlocksLoadingDots v-if="viewsCount === null" /> <span v-else style="margin-left: 2px">{{ numberFormat(viewsCount) }}</span>
              </div>
              <div class="__listing-added-date">
                <img v-lazy-load  :data-src="calendarIcon" alt="calendar icon">
                Added on: {{ formatDate(listing.created_at) }}
              </div>
            </div>

            <div class="__action-bar-and-video-button-wrapper">
              <!-- Video and 360 section -->
              <div v-if="listing.video360Url || listing.videoUrl" class="__video-button-wrapper">
                <ElementsButtonsIcon v-if="listing.videoUrl" :icon-src="videoIcon" label="Video" @click="playVideo('video')" />
                <div v-if="listing.videoUrl && listing.video360Url" class="__separator" />
                <ElementsButtonsIcon v-if="listing.video360Url" :icon-src="video360Icon" label="360 Tour" @click="playVideo('360')" />
              </div>

              <!-- Action bar -->
              <div class="__action-bar">
                <ElementsButtonsIcon v-if="listing.bedrooms" :icon-src="beds" :label="`${listing.bedrooms}`" not-clickable />
                <ElementsButtonsIcon v-if="listing.garageSpaces" :icon-src="parkings" :label="`${listing.garageSpaces}`" not-clickable />
                <ElementsButtonsIcon v-if="listing.bathrooms" :icon-src="bathrooms" :label="`${listing.bathrooms}`" not-clickable />
                <ElementsButtonsIcon v-if="listing.floorArea" :icon-src="landSize" :label="`${(listing.floorArea as string) || '-'}㎡`" not-clickable />

                <!-- Favorite button -->
                <div class="__favorite-button-wrapper" :class="{'--is-favorite': isAFavorite}">
                  <ElementsAvatar :icon-src="isAFavorite ? favoriteSelectedIcon : favoriteIcon" icon-alt="Favorite icon" @click="toggleAddToFavorite();" />
                </div>

                <CommonSocialSharePlatforms :url="shareUrl" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Image Gallery Popup -->
    <BlocksGalleryFullScreen v-if="galleryMedias.length > 0" v-model="isGalleryOpen" :selected-image-index="selectedImageIndex" :images="galleryMedias" :analytics-event-data="{entity: 'Listing', slug: listing.slug, displayedLocation: 'listing view', imageType: 'gallery'}" />
  </section>
</template>

<script setup lang="ts">
import eyeIcon from '~/assets/images/icons/eye.webp';
import calendarIcon from '~/assets/images/icons/calendar.webp';
import beds from '~/assets/images/icons/beds.webp';
import videoIcon from '~/assets/images/icons/video.webp';
import video360Icon from '~/assets/images/icons/360-video.webp';
import parkings from '~/assets/images/icons/parkings.webp';
import bathrooms from '~/assets/images/icons/bathrooms.webp';
import landSize from '~/assets/images/icons/land-size.webp';
import favoriteIcon from '~/assets/images/icons/favorite.webp';
import chevronDownIcon from '~/assets/images/icons/chevron-down.webp';
import galleryIcon from '~/assets/images/icons/gallery.webp';
import {computed, ComputedRef, PropType, Ref} from 'vue';
import {Listing} from '~/@types/listing';
import formatDate from '../../../composables/formatDate';
import {useAuthUserStore} from '~/store/authUser';
import favoriteSelectedIcon from 'assets/images/icons/favorite-selected.webp';
import globalApiHeaders from '~/composables/globalApiHeaders';
import numberFormat from '../../../composables/numberFormat';
import {ImageItem} from '~/@types/common';

// PROPS Definitions
//----------------------------------------------------------------------------------------------------------------------
const props = defineProps({
  listing: {
    type: [Object, null] as PropType<Listing>,
    default: null,
  },
});

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const authUserStore = useAuthUserStore();
const isGalleryOpen: Ref<boolean> = ref(false);
const selectedImageIndex: Ref<number> = ref(0);
const {$metaPixelTrackEvent} = useNuxtApp();
const config = useRuntimeConfig();
const router = useRouter();
const shareUrl: Ref<string> = ref('');
const viewsCount: Ref<number | null> = ref(null);
const bannerImages: Ref<{first: string | null; second: string | null; third: string | null; forth: string | null, fifth: string | null}> = ref({
  first: '',
  second: '',
  third: '',
  forth: '',
  fifth: '',
});

setBannerImages();

function playVideo (type: 'video' | '360') {
  if (type === 'video' && props.listing?.videoUrl) {
    selectedImageIndex.value = 2;
    isGalleryOpen.value = true;
  } else if (props.listing?.video360Url) {
    selectedImageIndex.value = props.listing.videoUrl ? 3 : 2;
    isGalleryOpen.value = true;
  }
}

/**
 * @_MOUNTED Hook
 */
onMounted(() => {
  if (!bannerImages.value.first) {
    setBannerImages();
  }

  shareUrl.value = window && window.location ? window.location.href : '';

  // Get view count
  getViewsCount();
});

/**
 * Get gallery medias
 */
const galleryMedias: ComputedRef<ImageItem[]> = computed(() => {
  if (props.listing) {
    const returnArray = props.listing.gallery.data.map((image) => ({
      url: image.url,
      type: 'image',
      caption: image.caption || '',
    } as ImageItem));

    const findHeroImage = props.listing?.gallery.data.find((image) => image.url === props.listing?.heroImageUrl);
    if (!findHeroImage && props.listing?.heroImageUrl) {
      returnArray.unshift({
        url: props.listing.heroImageUrl,
        type: 'image',
        caption: '',
      });
    }

    // Array to hold videos to be inserted
    const videosToInsert = [];

    if (props.listing.videoUrl) {
      videosToInsert.push({
        url: props.listing.videoUrl,
        type: 'video',
        caption: `${props.listing.name} video`,
      });
    }

    if (props.listing.video360Url) {
      videosToInsert.push({
        url: props.listing.video360Url,
        type: 'video',
        caption: `${props.listing.name} 360 tour`,
      });
    }

    // Insert videos at the second and third positions
    returnArray.splice(1, 0, ...videosToInsert);

    return returnArray;
  }
  return [];
});

/**
 * Set banner images
 */
function setBannerImages () {
  if (props.listing) {
    const galleryImages = props.listing.gallery.data.slice(0, 5).map((image) => image.url);
    if (galleryImages.length > 0) {
      bannerImages.value = {
        first: galleryImages[0] || null,
        second: galleryImages[1] || null,
        third: galleryImages[2] || null,
        forth: galleryImages[3] || null,
        fifth: galleryImages[4] || null,
      };
    } else {
      bannerImages.value.first = props.listing.heroImageUrl || null;
    }
  }
  return [];
}

/**
 * Add/Remove from favorite
 */
function toggleAddToFavorite () {
  const favorites = authUserStore.favorites;
  const did = props.listing.did ? props.listing.did : props.listing.id;
  if (favorites && favorites.length > 0 && did) {
    const index = favorites.findIndex((item) => item.did === did);
    if (index > -1) {
      authUserStore.setFavorites(favorites.filter((item) => item.did !== did));
    } else {
      authUserStore.setFavorites([...favorites, {
        type: props.listing?.listingTypes.data.includes('Design') ? 'design' : 'listing',
        did,
      }]);

      // Add to favorites Pixel event
      $metaPixelTrackEvent('AddToWishlist', {
        content_name: props.listing.name,
        content_category: props.listing.listingTypes.data.join(', ') ?? 'Listing',
        content_ids: [props.listing.did],
        content_type: 'Listing',
      });
    }
  } else if (did) {
    authUserStore.setFavorites([{
      type: props.listing?.listingTypes.data.includes('Design') ? 'design' : 'listing',
      did,
    }]);
  }
}

/**
 * Check whether this listing is a favorite or not
 */
const isAFavorite: ComputedRef<boolean> = computed(() => {
  const favorites = authUserStore.favorites;
  if (favorites && favorites.length > 0) {
    const did = props.listing.did ? props.listing.did : props.listing.did;
    const index = favorites.findIndex((item) => item.did === did);
    return index > -1;
  }
  return false;
});

function goBack () {
  router.back();
}

/**
 * Get views count
 */
function getViewsCount () {
  setTimeout(() => {
    if (props.listing?.slug) {
      $fetch(`/public/listings/view-count/${props.listing.slug}`,
        { baseURL: config.public.apiUrl, headers: globalApiHeaders()},
      ).then((response) => {
        viewsCount.value = response as number;
      });
    }
  }, 1500);
}
</script>

<style lang="scss">
.section-listing-banner {
  padding-top: 0 !important;
  height: 520px;
  padding-bottom: 38px;

  .__listing-banner-wrapper {
    position: relative;
    height: 100%;

    .__listing-image-wrapper {
      height: 100%;
      display: flex;
      cursor: pointer;

      .__column {
        height: 100%;
        width: 100%;
      }

      .__primary-image {
        background-size: cover;
        background-position: center;
      }

      .__secondary-images {
        display: flex;
        flex-wrap: wrap;

        .__secondary-image-item {
          height: 50%;
          width: 50%;
          background-size: cover;
          background-position: center;
          position: relative;

          .__more-image-count {
            width: 100%;
            height: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
            font-size: 24px;
            color: #fff;
            position: absolute;
            z-index: 1;
            background: rgba(0, 0, 0, 0.2);
          }
        }
      }
    }

    .__go-back-button-wrapper {
      position: absolute;
      top: 20px;
      width: 100%;
      height: fit-content;

      .container {
        display: flex;
        justify-content: space-between;
      }

      .__go-back-btn img {
        transform: rotate(90deg);
      }
    }

    .__banner-content-wrapper {
      padding: 100px 0 0;
      position: absolute;
      bottom: 0;
      width: 100%;
      height: fit-content;
      color: rgb(var(--text-light));
      z-index: 1;
      background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #272655 100%);

      .__listing-title {
        text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.25);
        font-size: 36px;
        font-weight: 500;
      }

      .__info-action-bar {
        display: flex;
        flex-wrap: wrap;
        width: 100%;
        justify-content: space-between;
        padding: 8px 0 5px;

        .__info-bar {
          display: flex;
          align-items: center;
          font-weight: 300;
          font-size: 14px;

          &>:not(:last-child) {
            margin-right: 30px;
          }

          .__listing-type {
            text-transform: uppercase;
            font-size: 15px;
          }

          &>* {
            display: flex;
            align-items: center;
          }

          .__listing-views {
            img {
              width: 18px;
              height: auto;
              margin-right: 8px;
            }
          }

          .__listing-added-date {
            img {
              width: 14px;
              height: auto;
              margin-right: 8px;
            }
          }
        }

        .__action-bar-and-video-button-wrapper {
          display: flex;
        }

        .__video-button-wrapper {
          margin-right: 24px;
          display: flex;
          padding: 4px 20px;
          border-radius: 20px;
          background: rgb(var(--background-light), 0.3);
          box-shadow: 0px -3px 4px rgba(0, 0, 0, 0.25);
          backdrop-filter: blur(2px);

          .__separator {
            width: 1px;
            height: 100%;
            background: #b2b2b2;
            margin-left: 12px;
            margin-right: 12px;
          }

          .el-icon-button {
            width: 80px;
            display: flex;
            justify-content: center;

            .__icon-button-label {
              color: #fff;
            }

            .__el-icon-button-inner {
              border-radius: 50%;
              padding: 8px;
              background: #fff;
              display: flex;
              justify-content: center;
              align-items: center;
            }

            &:hover {
              background: unset !important;
            }
          }

          @media screen and (max-width: $grid-md) {
            display: none;
          }
        }

        .__action-bar {
          margin-right: 24px;
          display: flex;
          padding: 4px 20px;
          border-radius: 20px;
          background: rgb(var(--background-light), 0.3);
          box-shadow: 0px -3px 4px rgba(0, 0, 0, 0.25);
          backdrop-filter: blur(2px);

          .el-icon-button {
            &:not(:last-child) {
              margin-right: 20px;
            }

            .__el-icon-button-inner {
              width: 25px;
              padding: 5px;

              img {
                width: 95%;
                height: auto;
              }
            }

            .__icon-button-label {
              color: rgb(var(--text-light));
              font-weight: 300;
              font-size: 14px;
            }
          }

          .el-avatar {
            border-left: 1px solid rgba(var(--text-light), 0.5);
            padding: 0 12px;
            align-items: center;
            display: flex;

            &:last-child {
              padding-right: 0;
            }
          }

          .__favorite-button-wrapper {
            margin-right: 8px;
            cursor: pointer;
            transition: all 0.4s ease-in-out !important;

            .el-avatar .__el-avatar-inner {
              scale: 1;
              transition: all 0.2s ease-in-out !important;
            }

            &.--is-favorite {
              .el-avatar {
                .__el-avatar-inner {
                  -webkit-box-shadow: inset 0 0 8px 0 rgba(0,0,0,0.4) !important;
                  box-shadow: inset 0 0 5px 0 rgba(0,0,0,0.4) !important;
                  transition: all 0.4s ease-in-out !important;
                }
              }
            }

            &:hover {
              .el-avatar {
                .__el-avatar-inner {
                  scale: 1.1;
                  transition: all 0.2s ease-in-out !important;
                }
              }
            }
          }
        }
      }

      // Responsive
      @media screen and (max-width: $grid-lg) {
        .__listing-title {
          text-align: center;
          width: 100%;
          margin-bottom: 8px;
        }

        .__info-action-bar {
          justify-content: center;
          .__info-bar {
            margin-bottom: 12px;
          }
        }
      }

      @media screen and (max-width: $grid-sm) {
        .__listing-title {
          font-size: 28px;
        }

        .__info-action-bar {
          padding-bottom: 0;

          .__info-bar {
            font-size: 12px;

            &>:not(:last-child) {
              margin-right: 15px;
            }

            .__listing-type {
              font-size: 14px;
            }
          }

          .__action-bar {
            padding: 2px 4px;
            border-radius: 20px;
            margin-right: 0;

            .el-icon-button {

              &:not(:last-child) {
                margin-right: 12px;
              }

              .__el-icon-button-inner {
                width: 20px !important;
                padding: 4px !important;

                img {
                  width: 100%;
                  height: auto;
                }
              }

              .__label {
                font-size: 12px !important;
              }
            }

            .el-avatar {
              padding: 0 5px;

              .__el-avatar-inner {
                width: 25px;
                height: 25px;
              }
            }
          }
        }
      }
    }
  }

  @media screen and (max-width: $grid-md) {
    height: 420px;

    .__listing-banner-wrapper .__listing-image-wrapper {
      flex-direction: column;
    }
  }
}
</style>
